import { UseFormReturnType } from "@mantine/form";
import { array, number, object, string, boolean, date } from "yup";
import NumberInput from "src/components/NumberInput/NumberInput";
import TextInput from "src/components/TextInput/TextInput";
import Switch from "src/components/Switch/Switch";
import Textarea from "src/components/Textarea/Textarea";
import Select from "src/components/Select";
import MultiSelect from "src/components/MultiSelect";
import DatePicker from "src/components/DatePicker";
import ActionCard from "src/components/ActionCard";
import { DiscountCodeType, ExpirationMode } from "src/generated/graphql";
import { Group } from "@mantine/core";
import { IIDAndName } from "src/utils/helperTypes";

interface DiscountFormFieldsProps<T> {
  form: UseFormReturnType<T>;
  locations?: IIDAndName[];
  isActive?: boolean;
  hideType?: boolean;
  children?: React.ReactNode;
}

const todayDate = new Date().toLocaleDateString();

export const discountFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  code: string().required("Code is required").max(20).notRequired(),
  value: number().required("Value is required"),
  locations: array()
    .min(1, "at least 1 Location is required")
    .required("Locations is required"),
  isPercent: boolean(),
  discountCodeType: string().required("Discount Type is required"),
  startAt: date()
    // .min(todayDate, `Start date should be greater than ${todayDate}`)
    .required("Start At is required"),
  // expiration
  mode: string(),
  expiresAt: date()
    .when("mode", {
      is: ExpirationMode.Date,
      then: () => date().required("First Allocation date is required"),
    })
    .nullable(),
  expirationUnit: string().when("mode", {
    is: ExpirationMode.Duration,
    then: () => string().required("Unit is required"),
  }),
  expirationValue: number()
    .nullable()
    .when("mode", {
      is: ExpirationMode.Duration,
      then: () => number().required("Value is required"),
    }),
});

const DiscountFormFields = <T,>({
  form,
  locations = [],
  isActive = false,
  hideType = false,
  children,
}: DiscountFormFieldsProps<T>) => {
  const { value: switchValue, ...switchProps } =
    form.getInputProps("isPercent");

  return (
    <>
      <ActionCard title="General" mb={10}>
        <TextInput
          label="Name"
          {...form.getInputProps("name")}
          disabled={isActive}
        />
        <Textarea
          label="description"
          disabled={isActive}
          {...form.getInputProps("description")}
        />
        <TextInput
          label="Code"
          {...form.getInputProps("code")}
          disabled={isActive}
        />

        <Group position="apart" align="center">
          <NumberInput
            w="60%"
            label="value"
            {...form.getInputProps("value")}
            disabled={isActive}
          />
          <Switch
            mt={15}
            labelPosition="left"
            disabled={isActive}
            label="Percentage"
            checked={switchValue}
            {...switchProps}
          />
        </Group>

        <MultiSelect
          disabled={isActive}
          label="Locations"
          name="locations"
          data={locations.map((location) => ({
            value: location.id,
            label: location.name,
          }))}
          {...form.getInputProps("locations")}
        />

        {!hideType && (
          <Select
            disabled={isActive}
            label="Type"
            data={Object.keys(DiscountCodeType)}
            {...form.getInputProps("discountCodeType")}
          />
        )}
        <DatePicker
          label="Start Date"
          minDate={new Date()}
          {...form.getInputProps("startAt")}
          disabled={isActive}
          dropdownPosition="top-start"
        />
      </ActionCard>

      {children}
    </>
  );
};

export default DiscountFormFields;
